import { useMemo, useState, useCallback, lazy, Suspense } from 'react';
import { useHistory } from 'react-router-dom';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonCard,
  IonCardContent,
  IonItem,
  IonInput,
  IonNote,
  IonIcon,
  IonBackButton,
  IonButtons,
  IonSelect,
  IonSelectOption,
  IonChip,
  IonGrid,
  IonRow,
  IonCol,
  IonToggle,
} from '@ionic/react';
import { search, location, filter, navigate, locate, map } from 'ionicons/icons';
import { useSearchCandidates, useMapCandidates } from '@/lib/api/hooks';
import {
  resolveSearcherOriginFromName,
  type SearcherOrigin,
} from '@/lib/api/geolocation';
import { useLocationContext } from '@/lib/geo/LocationContext';
import { workTypeEnum, PROVINCES } from '@/lib/validation/registration';
import { PlusCodeDisplay } from '@/components/candidate/PlusCodeDisplay';
import type { GeocodePrecisionTier } from '@/types';
import { useAuthStore } from '@/stores/useAuthStore';
import { NoindexSeo } from '@/components/NoindexSeo';
import { AppFooter } from '@/components/AppFooter';
import type { Map as MapInstance } from 'maplibre-gl';
import { cellToLatLng } from 'h3-js';

/** Lazy-loaded map components — code-split so they don't block initial paint. */
const LazyMapCanvas = lazy(() => import('@/components/map/MapCanvas').then((m) => ({ default: m.MapCanvas })));
const LazySearchRingLayer = lazy(() => import('@/components/map/SearchRingLayer').then((m) => ({ default: m.SearchRingLayer })));
const LazyH3CellsLayer = lazy(() => import('@/components/map/H3CellsLayer').then((m) => ({ default: m.H3CellsLayer })));

type OriginStatus = 'idle' | 'locating' | 'detected' | 'typed' | 'unavailable';

const RADIUS_OPTIONS = [5, 10, 25, 50];
const DEFAULT_RADIUS = 25; // Public-tier coarseness → 25km default

export function CandidateSearch() {
  const history = useHistory();
  const [query, setQuery] = useState('');
  const [province, setProvince] = useState('');
  const [workType, setWorkType] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const { h3Origin: ctxOrigin, name: ctxName, status: locStatus } = useLocationContext();

  const [origin, setOrigin] = useState<SearcherOrigin | null>(null);
  const [typedPlace, setTypedPlace] = useState('');
  const [typedError, setTypedError] = useState('');

  const [radiusKm, setRadiusKm] = useState<number>(DEFAULT_RADIUS);
  const [sortNearest, setSortNearest] = useState(false);
  const [showMap, setShowMap] = useState(true);
  const [mapInstance, setMapInstance] = useState<MapInstance | null>(null);

  // Derive origin from LocationContext (single geolocation prompt for entire app).
  const resolvedOrigin: SearcherOrigin | null = useMemo(() => {
    if (origin) return origin; // manual typed override
    if (ctxOrigin) return { h3Origin: ctxOrigin, label: ctxName ?? undefined };
    return null;
  }, [origin, ctxOrigin, ctxName]);

  const originStatus: OriginStatus = useMemo(() => {
    if (origin) return 'typed';
    if (locStatus === 'loading') return 'locating';
    if (locStatus === 'ready' && ctxOrigin) return 'detected';
    return 'unavailable';
  }, [origin, locStatus, ctxOrigin]);

  // Typed-place fallback (denied/unavailable geolocation, or manual override).
  async function applyTypedPlace() {
    const name = typedPlace.trim();
    if (!name) return;
    setTypedError('');
    const resolved = await resolveSearcherOriginFromName(name);
    if (resolved) {
      setOrigin(resolved);
    } else {
      setTypedError('Could not find that place. Try a city or suburb.');
    }
  }

  const { isAuthenticated } = useAuthStore();
  const viewerTier: GeocodePrecisionTier = isAuthenticated ? 'Private' : 'Public';

  const { data, isLoading } = useSearchCandidates({
    query: query || undefined,
    province: (province || undefined) as never,
    workType: (workType || undefined) as never,
    h3Origin: resolvedOrigin?.h3Origin,
    radiusKm,
    page: 1,
    pageSize: 20,
  });

  // Map data: candidate cells within the k-ring around the search origin (API caps k at 10).
  const { data: mapData } = useMapCandidates(
    resolvedOrigin?.h3Origin,
    Math.min(10, Math.ceil(radiusKm / 3.23)),
    !!resolvedOrigin?.h3Origin && showMap,
  );

  // Compute map centre from the origin H3 cell (res-6 → [lng, lat] for MapLibre).
  const mapCenter: [number, number] = useMemo(() => {
    if (!resolvedOrigin?.h3Origin) return [22.9, -30.5];
    try {
      const [lat, lng] = cellToLatLng(resolvedOrigin.h3Origin);
      return [lng, lat];
    } catch {
      return [22.9, -30.5];
    }
  }, [resolvedOrigin?.h3Origin]);

  const handleMapReady = useCallback((map: MapInstance) => {
    setMapInstance(map);
  }, []);

  const rawResults = data?.data.hits ?? [];

  const results = useMemo(() => {
    if (!sortNearest || originStatus === 'unavailable') return rawResults;
    return [...rawResults].sort((a, b) => (a.distanceKm ?? Infinity) - (b.distanceKm ?? Infinity));
  }, [rawResults, sortNearest, originStatus]);

  return (
    <IonPage>
      <NoindexSeo />
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonBackButton defaultHref="/employer" /></IonButtons>
          <IonTitle>Search Candidates</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <IonInput placeholder="Search by skill, trade, or name" value={query} onIonInput={(e) => setQuery(e.detail.value!)} style={{ flex: 1 }}>
            <IonIcon icon={search} slot="start" />
          </IonInput>
          <IonButton fill="outline" onClick={() => setShowFilters(!showFilters)}>
            <IonIcon icon={filter} />
          </IonButton>
        </div>

        {/* Geolocation: detected place name (never raw coords) or typed fallback */}
        <IonCard color="light">
          <IonCardContent>
            {originStatus === 'locating' && (
              <IonNote>Detecting your location…</IonNote>
            )}
            {originStatus === 'detected' && resolvedOrigin?.label && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <IonIcon icon={locate} />
                <IonNote>Searching near <strong>{resolvedOrigin.label}</strong></IonNote>
                <IonChip outline onClick={() => setOrigin(null)}>Change</IonChip>
              </div>
            )}
            {originStatus === 'unavailable' && (
              <div>
                <IonNote>Location unavailable — enter a place to search near:</IonNote>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <IonInput
                    placeholder="City, suburb, or town"
                    value={typedPlace}
                    onIonInput={(e) => setTypedPlace(e.detail.value!)}
                    onKeyUp={(e) => e.key === 'Enter' && applyTypedPlace()}
                    style={{ flex: 1 }}
                  >
                    <IonIcon icon={navigate} slot="start" />
                  </IonInput>
                  <IonButton onClick={applyTypedPlace}>Use place</IonButton>
                </div>
                {typedError && <IonNote color="danger">{typedError}</IonNote>}
              </div>
            )}
            {originStatus === 'typed' && origin?.label && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
                <IonIcon icon={locate} />
                <IonNote>Searching near <strong>{origin.label}</strong></IonNote>
              </div>
            )}

            {/* Radius control — distance-based, not text */}
            <IonItem>
              <IonSelect
                label="Radius"
                value={radiusKm}
                onIonChange={(e) => setRadiusKm(Number(e.detail.value))}
              >
                {RADIUS_OPTIONS.map((r) => (
                  <IonSelectOption key={r} value={r}>{r} km</IonSelectOption>
                ))}
                <IonSelectOption value={-1}>Custom</IonSelectOption>
              </IonSelect>
            </IonItem>
            {radiusKm === -1 && (
              <IonItem>
                <IonInput
                  type="number"
                  label="Custom radius (km)"
                  value={radiusKm === -1 ? '' : radiusKm}
                  onIonInput={(e) => setRadiusKm(Number(e.detail.value) || DEFAULT_RADIUS)}
                />
              </IonItem>
            )}

            <IonItem lines="none">
              <IonToggle
                checked={sortNearest}
                onIonChange={(e) => setSortNearest(e.detail.checked)}
                labelPlacement="start"
              >
                Nearest first
              </IonToggle>
            </IonItem>
          </IonCardContent>
        </IonCard>

        {showFilters && (
          <IonCard color="light">
            <IonCardContent>
              <IonItem>
                <IonSelect label="Province" value={province} onIonChange={(e) => setProvince(e.detail.value)}>
                  <IonSelectOption value="">All</IonSelectOption>
                  {PROVINCES.map((p) => <IonSelectOption key={p} value={p}>{p}</IonSelectOption>)}
                </IonSelect>
              </IonItem>
              <IonItem>
                <IonSelect label="Work type" value={workType} onIonChange={(e) => setWorkType(e.detail.value)}>
                  <IonSelectOption value="">All</IonSelectOption>
                  {workTypeEnum.map((w) => <IonSelectOption key={w} value={w}>{w}</IonSelectOption>)}
                </IonSelect>
              </IonItem>
            </IonCardContent>
          </IonCard>
        )}

        {/* Interactive map — shows search rings and candidate cells when origin is set */}
        {origin?.h3Origin && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <IonNote style={{ fontSize: '0.75rem', color: 'var(--bp-text-muted)' }}>
                <IonIcon icon={map} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                Search area — {radiusKm} km radius
              </IonNote>
              <IonButton fill="clear" size="small" onClick={() => setShowMap(!showMap)}>
                {showMap ? 'Hide map' : 'Show map'}
              </IonButton>
            </div>
            {showMap && (
              <Suspense fallback={<div style={{ height: 400, background: 'var(--bp-surface)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--bp-text-muted)', fontSize: '0.8125rem' }}>Loading map…</div>}>
                <LazyMapCanvas
                  center={mapCenter}
                  zoom={radiusKm <= 10 ? 12 : radiusKm <= 25 ? 11 : 10}
                  style={{ height: 400 }}
                  onMapReady={handleMapReady}
                >
                  {mapInstance && mapData && (
                    <>
                      <LazySearchRingLayer map={mapInstance} data={mapData} />
                      <LazyH3CellsLayer
                        map={mapInstance}
                        data={mapData}
                        fillColor="#3b82f6"
                        fillOpacity={0.4}
                      />
                    </>
                  )}
                </LazyMapCanvas>
              </Suspense>
            )}
          </div>
        )}

        {isLoading ? (
          <p className="bp-subheading">Searching…</p>
        ) : results.length === 0 ? (
          <IonCard>
            <IonCardContent className="ion-text-center">
              <p className="bp-subheading">No candidates found. Try different filters.</p>
            </IonCardContent>
          </IonCard>
        ) : (
          <IonGrid>
            <IonRow>
              {results.map((c) => (
                <IonCol size="12" size-md="6" key={c._id}>
                  <IonCard button onClick={() => history.push(`/candidate/${c.slug}`)}>
                    <IonCardContent>
                      <h3 style={{ margin: 0, fontWeight: 600 }}>{c.headline || 'Skilled tradesperson'}</h3>
                      <IonNote>{c.profileType} · {c.availability}</IonNote>
                      <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <IonChip color="primary" outline>{c.workType}</IonChip>
                        {typeof c.distanceKm === 'number' && (
                          <IonChip outline><IonIcon icon={location} />{c.distanceKm.toFixed(1)} km</IonChip>
                        )}
                        {c.desiredLocations?.slice(0, 2).map((loc) => (
                          <IonChip key={loc.h3Index} outline>
                            <PlusCodeDisplay
                              plusCodeShort={loc.plusCodeShort}
                              geocodePrecision={loc.geocodePrecision}
                              viewerTier={(loc.geocodePrecision ?? viewerTier) as GeocodePrecisionTier}
                            />
                          </IonChip>
                        ))}
                      </div>
                    </IonCardContent>
                  </IonCard>
                </IonCol>
              ))}
            </IonRow>
          </IonGrid>
        )}
        <AppFooter />
      </IonContent>
    </IonPage>
  );
}
